import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Skeleton from 'react-loading-skeleton';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, ResponsiveContainer, Cell,
} from 'recharts';
import authStorage from '../../utils/authStorage';
import { BASE_URL } from '../../config';

/**
 * My Performance - the member's own goal history and consistency.
 *
 * Reads `GET /performance?period=3|6|12`. EVERY number here comes from the API;
 * nothing is derived in the browser. That is deliberate: Android and iOS consume
 * the same endpoint, and a percentage computed client-side would drift between
 * platforms the first time someone rounded differently.
 */

const PERIODS = [3, 6, 12];

/*
 * Status palette, validated rather than chosen by eye.
 *
 * The obvious green/red (#10b981 / #ef4444) scores deutan dE 8.1 - right on the
 * colourblind floor - and emerald fails contrast against a light surface at
 * 2.47:1. These darker steps score dE 10.4 and pass contrast. Do not "brighten"
 * them back.
 *
 * Status colour is never the only signal either: every bar carries its
 * percentage as a direct label, the legend names both states, and the history
 * table below repeats the outcome as words.
 */
/**
 * Why the denominator is smaller than the window asked for.
 *
 * TWO different reasons now, and blaming the wrong one is worse than saying
 * nothing: months before `startMonth` are missing because the platform does not
 * report that far back, NOT because the member failed them or joined late. The
 * epoch reason wins when it applies, because it explains the larger share.
 */
const shortfallReason = (data) => {
    if (!data || data.eligibleMonths >= data.requestedMonths) return 'completed months';
    if (data.startMonth && startsAtEpoch(data)) return `history starts ${fmtMonth(data.startMonth)}`;
    return `${data.requestedMonths - data.eligibleMonths} month(s) before you joined excluded`;
};

/**
 * True when the window is short because it ran into the start month rather than
 * the member's own join date - i.e. the oldest month shown IS the start month.
 */
const startsAtEpoch = (data) => {
    const oldest = data.months && data.months.length ? data.months[0].month : null;
    return Boolean(oldest) && oldest <= data.startMonth;
};

const MET = '#059669';
const SHORT = '#b91c1c';
/*
 * The month still running. NOT amber or red: a month at 60% on the 18th has not
 * failed, and painting it in the "below goal" colour asserts a verdict that has
 * not been earned yet. Blue says "not judged".
 *
 * Validated with the palette checker against MET and SHORT: all six checks pass
 * (worst adjacent deutan dE 10.4, contrast >= 3:1). #b45309 was tried first and
 * FAILED against SHORT at deutan dE 4.9 -- it really did read as "below goal".
 */
const RUNNING = '#1d4ed8';

const fmtMonth = (m, short = false) => {
    if (!m) return '';
    const [y, mo] = m.split('-').map(Number);
    const d = new Date(y, mo - 1, 1);
    const name = d.toLocaleString('en', { month: short ? 'short' : 'long' });
    return short ? `${name}\n${y}` : `${name} ${y}`;
};

const MyPerformance = () => {
    const [period, setPeriod] = useState(12);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError('');

        axios({
            url: `${BASE_URL}/performance`,
            method: 'GET',
            headers: { 'x-access-token': authStorage.getItem('auth-token') },
            params: { period },
        })
            .then((res) => {
                if (cancelled) return;
                setData(res?.data?.data || null);
                setLoading(false);
            })
            .catch((err) => {
                if (cancelled) return;
                setLoading(false);
                // A 404 here means an admin switched the module off mid-session.
                setError(
                    err?.response?.status === 404
                        ? 'This feature is not currently available.'
                        : 'Could not load your performance history. Please try again.'
                );
            });

        return () => { cancelled = true; };
    }, [period]);

    /*
     * Closed months plus the one still running, oldest first.
     *
     * The API keeps `inProgress` OUT of `months` on purpose -- it is excluded
     * from every headline figure, and that contract does not change here. This
     * is a display-only merge so the chart and the table show the current month
     * too; each row carries `running` so both can mark it as unfinished.
     */
    const displayMonths = [
        ...(data?.months || []),
        ...(data?.inProgress ? [data.inProgress] : []),
    ];

    const chartData = displayMonths.map((m) => ({
        month: fmtMonth(m.month, true),
        rawMonth: m.month,
        pct: m.progressPercentage,
        met: m.goalAchieved,
        running: Boolean(data?.inProgress && m.month === data.inProgress.month),
        qualified: m.qualifiedMinutes,
        target: m.goalMinutes,
        device: m.device,
        basis: m.goalBasis,
    }));

    /*
     * A goal once met cannot be un-met by the rest of the month, so a running
     * month that has already passed 100% keeps the green. Only the undecided
     * case gets the "still running" colour.
     */
    const barColour = (d) => (d.met ? MET : d.running ? RUNNING : SHORT);

    /** Per-mark hover detail. A bar alone cannot say which rule produced it. */
    const ChartTooltip = ({ active, payload }) => {
        if (!active || !payload?.length) return null;
        const d = payload[0].payload;
        return (
            <div className="rounded-md border border-gray-200 bg-white px-3 py-2 text-xs shadow-lg">
                <div className="font-semibold text-gray-900">{fmtMonth(d.rawMonth)}</div>
                <div className="mt-1 text-gray-700">
                    <span className="font-medium tabular-nums">{d.qualified}</span> of {d.target} counted minutes
                </div>
                <div className="tabular-nums" style={{ color: barColour(d) }}>
                    {d.pct}%{' · '}
                    {d.met
                        ? (d.running ? 'Goal met already' : 'Goal met')
                        : (d.running ? 'Still running' : 'Below goal')}
                </div>
                {d.running && (
                    <div className="text-gray-500">This month so far — not final</div>
                )}
                <div className="mt-1 text-gray-500">{d.device}</div>
                {d.basis && <div className="mt-1 max-w-[240px] text-gray-400">{d.basis}</div>}
            </div>
        );
    };

    const StatCard = ({ label, value, sub, hint }) => (
        <div className="rounded-xl border border-gray-200 bg-white p-4" title={hint}>
            <p className="text-xl font-bold text-gray-900 tabular-nums">{value}</p>
            <p className="mt-0.5 text-xs font-medium text-gray-700">{label}</p>
            {sub && <p className="mt-0.5 text-[11px] text-gray-500">{sub}</p>}
        </div>
    );

    if (loading) {
        return <div className="py-4"><Skeleton count={6} height={44} /></div>;
    }

    if (error) {
        return (
            <div className="my-4 rounded border border-red-300 bg-red-50 p-3 text-sm text-red-700">
                {error}
            </div>
        );
    }

    if (!data) return null;

    // A member in their first month has no CLOSED months but does have a running
    // one, and that is worth drawing rather than showing the empty state.
    const noHistory = !displayMonths.length;

    return (
        <div>
            {/* Period selector */}
            <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h3 className="text-base font-semibold text-gray-900">Performance Analytics</h3>
                    <p className="mt-0.5 text-xs text-gray-500">
                        How consistently you have achieved your monthly goal over time.
                    </p>
                    {/*
                      * Without this the three period buttons look broken: until twelve
                      * months have passed since the start month they all return the same
                      * months and therefore identical figures. Say why, once, rather than
                      * letting a member conclude the selector does nothing.
                      */}
                    {startsAtEpoch(data) && (
                        <p className="mt-0.5 text-xs text-gray-500">
                            Analytics begin {fmtMonth(data.startMonth)}, so longer windows
                            currently cover the same months.
                        </p>
                    )}
                </div>
                <div className="flex gap-2" role="group" aria-label="Period">
                    {PERIODS.map((p) => (
                        <button
                            key={p}
                            type="button"
                            onClick={() => setPeriod(p)}
                            className="cursor-pointer rounded-md border-0 appearance-none px-4 py-2 text-sm font-medium"
                            style={
                                period === p
                                    ? { background: '#E53E14', color: '#fff' }
                                    : { background: '#f3f4f6', color: '#4b5563' }
                            }
                        >
                            {p} Months
                        </button>
                    ))}
                </div>
            </div>

            {/*
              * The in-progress month, shown APART from every figure below.
              * It is deliberately excluded from the success rate, the average and
              * the streak: a member three days into a month has barely any
              * minutes, and counting that as a miss would break a live streak.
              */}
            {data.inProgress && (
                <div className="mb-5 rounded-xl border-0 border-l-4 border-solid bg-amber-50 p-4"
                     style={{ borderLeftColor: '#E53E14' }}>
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-600">
                        {fmtMonth(data.inProgress.month)} · in progress
                    </p>
                    <p className="mt-1 text-sm text-gray-800">
                        <span className="font-semibold tabular-nums">{data.inProgress.progressPercentage}%</span>
                        {' '}of goal so far —{' '}
                        <span className="tabular-nums">{data.inProgress.qualifiedMinutes}</span> of{' '}
                        <span className="tabular-nums">{data.inProgress.goalMinutes}</span> counted minutes.
                    </p>
                    <p className="mt-1 text-xs text-gray-500">
                        This month is still running, so it is not counted in the figures below and cannot
                        break your streak.
                    </p>
                </div>
            )}

            {noHistory ? (
                <div className="py-14 text-center text-gray-500">
                    <p className="text-sm">No completed months yet.</p>
                    <p className="mt-1 text-xs">
                        Your history builds up as each month closes — check back once this month ends.
                    </p>
                </div>
            ) : (
                <>
                    {/* Headline figures */}
                    <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
                        <StatCard
                            label="Goals Achieved"
                            value={`${data.goalsAchieved} of ${data.eligibleMonths}`}
                            sub={shortfallReason(data)}
                            hint="Months you met your goal, out of the completed months available in this window."
                        />
                        <StatCard label="Goal Success Rate" value={`${data.successRate}%`} sub="of eligible months"
                                  hint="Goals achieved divided by eligible completed months." />
                        <StatCard label="Current Streak" value={`${data.currentStreak} mo`} sub="consecutive"
                                  hint="Counts back from the most recently completed month. The month in progress cannot break it." />
                        <StatCard label="Best Streak" value={`${data.bestStreak} mo`} sub="longest run"
                                  hint="The longest run of consecutive achieved months in this window." />
                        <StatCard label="Average Progress" value={`${data.averageGoalProgress}%`} sub="mean of months"
                                  hint="Mean goal percentage across eligible completed months." />
                        <StatCard label="Qualified Minutes" value={data.totalQualifiedMinutes.toLocaleString()} sub="total, incl. this month"
                                  hint="Counted minutes across the window. A running total, so it does include the month in progress." />
                    </div>

                    <div className="grid grid-cols-1 gap-5 xl:grid-cols-3">
                        {/* Monthly goal progress */}
                        <div className="rounded-xl border border-gray-200 bg-white p-4 xl:col-span-2">
                            <h4 className="text-sm font-semibold text-gray-900">Monthly Goal Progress</h4>
                            <p className="mt-0.5 text-xs text-gray-500">
                                Each month as a percentage of that month&rsquo;s own target — the target itself
                                changes with your connected device.
                            </p>
                            <div className="mt-4" style={{ width: '100%', height: 260 }}>
                                <ResponsiveContainer>
                                    <BarChart data={chartData} margin={{ top: 18, right: 8, left: -18, bottom: 0 }} barCategoryGap="22%">
                                        <CartesianGrid vertical={false} stroke="#f1f1f1" />
                                        <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#6b7280' }}
                                               interval={0} tickLine={false} axisLine={{ stroke: '#e5e7eb' }} />
                                        <YAxis tick={{ fontSize: 10, fill: '#6b7280' }} tickLine={false}
                                               axisLine={false} unit="%" domain={[0, (max) => Math.max(140, Math.ceil(max / 20) * 20)]} />
                                        <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(0,0,0,0.035)' }} />
                                        {/* The 100% line is the whole point of the chart. */}
                                        <ReferenceLine y={100} stroke="#9ca3af" strokeDasharray="4 4"
                                                       label={{ value: 'Goal 100%', position: 'right', fontSize: 10, fill: '#6b7280' }} />
                                        <Bar dataKey="pct" radius={[4, 4, 0, 0]} label={{ position: 'top', fontSize: 10, fill: '#374151', formatter: (v) => `${v}%` }}>
                                            {chartData.map((d) => (
                                                <Cell
                                                    key={d.rawMonth}
                                                    fill={barColour(d)}
                                                    /* Colour alone would not separate a running month
                                                       that has already met its goal from a closed one,
                                                       so the unfinished bar also gets a dashed outline. */
                                                    stroke={d.running ? barColour(d) : undefined}
                                                    strokeDasharray={d.running ? '3 2' : undefined}
                                                    fillOpacity={d.running ? 0.55 : 1}
                                                />
                                            ))}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                            {/* Legend - identity is never colour alone. */}
                            <div className="mt-2 flex flex-wrap items-center gap-4 text-xs text-gray-600">
                                <span className="flex items-center gap-1.5">
                                    <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: MET }} />
                                    Goal met (100% or more)
                                </span>
                                <span className="flex items-center gap-1.5">
                                    <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: SHORT }} />
                                    Below goal
                                </span>
                                <span className="flex items-center gap-1.5">
                                    <span className="inline-block h-0 w-4 border-0 border-t border-dashed border-gray-400" />
                                    Goal (100%)
                                </span>
                                {data.inProgress && (
                                    <span className="flex items-center gap-1.5">
                                        <span className="inline-block h-2.5 w-2.5 rounded-sm border border-dashed"
                                              style={{ background: RUNNING, opacity: 0.55, borderColor: RUNNING }} />
                                        This month so far (not final)
                                    </span>
                                )}
                            </div>
                        </div>

                        {/* Consistency across the three windows */}
                        <div className="rounded-xl border border-gray-200 bg-white p-4">
                            <h4 className="text-sm font-semibold text-gray-900">Goal Consistency</h4>
                            <p className="mt-0.5 text-xs text-gray-500">
                                The same measure over each window, so a recent run is visible next to the longer trend.
                            </p>
                            <div className="mt-4 space-y-4">
                                {data.consistency.map((c) => (
                                    <div key={c.period}>
                                        <div className="flex items-baseline justify-between text-xs">
                                            <span className="font-medium text-gray-800">Last {c.period} Months</span>
                                            <span className="tabular-nums text-gray-600">
                                                {c.goalsAchieved} of {c.eligibleMonths}
                                                <span className="ml-2 font-semibold text-gray-900">{c.successRate}%</span>
                                            </span>
                                        </div>
                                        <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-gray-200">
                                            <div className="h-full rounded-full"
                                                 style={{ width: `${Math.min(c.successRate, 100)}%`, background: MET }} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* The table view - also the accessible alternative to the chart. */}
                    <div className="mt-5 rounded-xl border border-gray-200 bg-white p-4">
                        <h4 className="text-sm font-semibold text-gray-900">Monthly Goal History</h4>
                        <div className="mt-3 overflow-x-auto">
                            <table className="w-full text-left text-sm" style={{ minWidth: '720px' }}>
                                <thead className="bg-[#FFF5F3] text-xs uppercase text-gray-700">
                                    <tr>
                                        <th scope="col" className="p-3 rounded-tl-xl">Month</th>
                                        <th scope="col" className="p-3 text-center">Goal</th>
                                        <th scope="col" className="p-3 text-center">Qualified</th>
                                        <th scope="col" className="p-3 text-center">Progress</th>
                                        <th scope="col" className="p-3 text-center">Result</th>
                                        <th scope="col" className="p-3 text-center">Active Days</th>
                                        <th scope="col" className="p-3 text-center">Device</th>
                                        <th scope="col" className="p-3 text-center">Global</th>
                                        <th scope="col" className="p-3 text-center rounded-tr-xl">Org</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {/* Newest first, so the month in progress heads the list. */}
                                    {[...displayMonths].reverse().map((m) => {
                                      const running = Boolean(data.inProgress && m.month === data.inProgress.month);
                                      return (
                                        <tr key={m.month} className="border-0 border-b border-solid border-gray-100"
                                            style={running ? { background: '#f8fafc' } : undefined}>
                                            <td className="p-3 font-medium text-gray-900">
                                                {fmtMonth(m.month)}
                                                {running && (
                                                    <span className="ml-2 text-xs font-normal" style={{ color: RUNNING }}>
                                                        so far
                                                    </span>
                                                )}
                                            </td>
                                            <td className="p-3 text-center tabular-nums text-gray-600">{m.goalMinutes}</td>
                                            <td className="p-3 text-center tabular-nums text-gray-900">{m.qualifiedMinutes}</td>
                                            <td className="p-3 text-center">
                                                <span className="font-semibold tabular-nums"
                                                      style={{ color: m.goalAchieved ? MET : running ? RUNNING : SHORT }}>
                                                    {m.progressPercentage}%
                                                </span>
                                            </td>
                                            <td className="p-3 text-center">
                                                {/* A running month below 100% is NOT "below goal" --
                                                    it has not finished. Only a closed month earns
                                                    that verdict. */}
                                                <span className="rounded-full px-2 py-0.5 text-xs font-medium"
                                                      style={m.goalAchieved
                                                          ? { background: '#d1fae5', color: '#065f46' }
                                                          : running
                                                              ? { background: '#dbeafe', color: '#1e40af' }
                                                              : { background: '#fee2e2', color: '#991b1b' }}>
                                                    {m.goalAchieved ? 'Goal met' : running ? 'In progress' : 'Below goal'}
                                                </span>
                                            </td>
                                            <td className="p-3 text-center tabular-nums text-gray-700">{m.activeDays}</td>
                                            <td className="p-3 text-center text-xs text-gray-600">{m.device}</td>
                                            <td className="p-3 text-center tabular-nums text-gray-700">{m.globalRank ?? '—'}</td>
                                            <td className="p-3 text-center tabular-nums text-gray-700">{m.organizationRank ?? '—'}</td>
                                        </tr>
                                      );
                                    })}
                                </tbody>
                            </table>
                        </div>
                        <p className="mt-3 text-xs text-gray-500">
                            The goal target is not fixed: Fitbit-only members are measured in Active Zone Minutes
                            (Moderate &times;1, Vigorous &times;2, Peak &times;2) against 600, while Garmin, Apple and
                            mixed-device members are measured in raw minutes against 300 if they logged any Vigorous
                            time, otherwise 600. Hover a bar to see which rule applied that month.
                        </p>
                        {data.inProgress && (
                            <p className="mt-2 text-xs text-gray-500">
                                The current month is shown as it stands today and is not final, so it is
                                left out of the success rate, the average and the streaks above. Its
                                ranking positions stay blank until the month closes.
                            </p>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default MyPerformance;
